import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DAY_LABELS_MEDIUM_ES,
  minutesToHHMM,
} from '../../utils/schedule';

/**
 * Render visual de un calendario semanal de horarios. Una fila por día
 * (Lun..Dom), un track horizontal con bloques pintados por sede.
 *
 * Comportamientos:
 *  - Si no hay rangos en sábado/domingo, esos días NO se muestran (mínimo Lun-Vie).
 *  - Track auto-encuadra al rango usado (snap a hora ±1h, mínimo 4h).
 *  - Cada sede tiene un color determinístico por orden en `sedes`.
 *  - Hover sobre un bloque: tooltip "{start}–{end} · {sede}".
 *
 * Props:
 *   days: List<List<{start,end,sede?}>> — array de 7 días (Lun=0..Dom=6).
 *   sedes: string[] — para mapear sede→color y detectar huérfanas.
 *   compact: bool — variante para columnas estrechas.
 */

const SEDE_PALETTE = [
  '#3b82f6', '#10b981', '#f59e0b', '#ec4899',
  '#8b5cf6', '#06b6d4', '#ef4444', '#84cc16',
];
const COLOR_NO_SEDE = '#6b7280';
const COLOR_ORPHAN  = '#9ca3af';

function ScheduleHeatmap({ days = [], sedes = [], compact = false }) {
  const { t } = useTranslation();

  const sedeColor = useMemo(() => {
    const m = new Map();
    (sedes || []).forEach((s, i) => {
      m.set(s, SEDE_PALETTE[i % SEDE_PALETTE.length]);
    });
    return m;
  }, [sedes]);

  const { minMin, maxMin, hourTicks } = useMemo(() => {
    let lo = Infinity, hi = -Infinity;
    for (const d of days) for (const r of (d || [])) {
      if (r.start < lo) lo = r.start;
      if (r.end   > hi) hi = r.end;
    }
    if (!isFinite(lo)) { lo = 8 * 60; hi = 22 * 60; }
    lo = Math.max(0, Math.floor((lo - 60) / 60) * 60);
    hi = Math.min(24 * 60, Math.ceil((hi + 60) / 60) * 60);
    if (hi - lo < 240) hi = Math.min(24 * 60, lo + 240);
    const ticks = [];
    for (let h = Math.ceil(lo / 60); h * 60 <= hi; h += 2) ticks.push(h);
    return { minMin: lo, maxMin: hi, hourTicks: ticks };
  }, [days]);

  // Lun-Vie por default; +Sáb/Dom solo si hay rangos.
  const visibleDays = useMemo(() => {
    if ((days[6] || []).length > 0) return 7;
    if ((days[5] || []).length > 0) return 6;
    return 5;
  }, [days]);

  const dayColW = compact ? 'w-9' : 'w-10';
  const trackH = compact ? 'h-6' : 'h-7';

  const totalRanges = days.reduce((acc, d) => acc + (d?.length || 0), 0);
  if (totalRanges === 0) {
    return (
      <p className="text-xs text-on-surface-variant/60 italic py-2">
        {t('Sin horarios.')}
      </p>
    );
  }

  return (
    <div>
      {/* Hour ruler */}
      <div className="flex items-center mb-1">
        <div className={`${dayColW} shrink-0`} />
        <div className="flex-1 relative h-3.5">
          {hourTicks.map((hr) => {
            const pct = ((hr * 60 - minMin) / (maxMin - minMin)) * 100;
            return (
              <span
                key={hr}
                className="absolute top-0 -translate-x-1/2 text-[9px] text-on-surface-variant/70 tabular-nums"
                style={{ left: `${pct}%` }}
              >
                {hr}h
              </span>
            );
          })}
        </div>
      </div>

      {/* Day rows (solo días visibles) */}
      <div className="space-y-1">
        {Array.from({ length: visibleDays }).map((_, d) => {
          const day = days[d] || [];
          return (
            <div key={d} className="flex items-center gap-2">
              <span className={`${dayColW} shrink-0 text-[11px] font-bold text-on-surface-variant tabular-nums`}>
                {DAY_LABELS_MEDIUM_ES[d]}
              </span>
              <div className={`flex-1 relative ${trackH} bg-surface-container-high/30 rounded-md overflow-hidden`}>
                {hourTicks.map((hr) => {
                  const pct = ((hr * 60 - minMin) / (maxMin - minMin)) * 100;
                  return (
                    <span
                      key={`grid-${hr}`}
                      className="absolute top-0 bottom-0 border-l border-outline-variant/15"
                      style={{ left: `${pct}%` }}
                    />
                  );
                })}
                {day.map((r, i) => {
                  const left = ((r.start - minMin) / (maxMin - minMin)) * 100;
                  const width = ((r.end - r.start) / (maxMin - minMin)) * 100;
                  let color = COLOR_NO_SEDE;
                  let suffix = '';
                  if (r.sede) {
                    if (sedeColor.has(r.sede)) color = sedeColor.get(r.sede);
                    else { color = COLOR_ORPHAN; suffix = ` (${t('eliminada')})`; }
                  }
                  const tooltip = `${minutesToHHMM(r.start)}–${minutesToHHMM(r.end)}${
                    r.sede ? ` · ${r.sede}${suffix}` : ''
                  }`;
                  return (
                    <div
                      key={i}
                      title={tooltip}
                      className="absolute top-0.5 bottom-0.5 rounded-sm hover:brightness-110 hover:ring-1 hover:ring-on-surface/20 transition-all cursor-default"
                      style={{
                        left: `${left}%`,
                        width: `${Math.max(width, 0.5)}%`,
                        backgroundColor: color,
                      }}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      {sedes && sedes.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1.5">
          {sedes.map((s, i) => (
            <span key={s} className="inline-flex items-center gap-1.5 text-[10px] text-on-surface-variant">
              <span
                className="w-2.5 h-2.5 rounded-sm shrink-0"
                style={{ backgroundColor: SEDE_PALETTE[i % SEDE_PALETTE.length] }}
              />
              <span className="truncate max-w-[120px]">{s}</span>
            </span>
          ))}
          {(() => {
            const hasNoSede = days.some((d) => (d || []).some((r) => !r.sede));
            const hasOrphan = days.some((d) => (d || []).some((r) => r.sede && !sedeColor.has(r.sede)));
            return (
              <>
                {hasNoSede && (
                  <span className="inline-flex items-center gap-1.5 text-[10px] text-on-surface-variant">
                    <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: COLOR_NO_SEDE }} />
                    {t('Sin sede')}
                  </span>
                )}
                {hasOrphan && (
                  <span className="inline-flex items-center gap-1.5 text-[10px] text-on-surface-variant/80 italic">
                    <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: COLOR_ORPHAN }} />
                    {t('Sede eliminada')}
                  </span>
                )}
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}

export default ScheduleHeatmap;
